import {SignupModal} from '@commercial-website/ui'
import {graphql, useStaticQuery} from 'gatsby'
import React, {useEffect, useRef, useState} from 'react'

import {
    signupFormModalOpened,
    SignupFormModalOpenedAction,
    signupFormSubmitted,
} from '@/analytics/segment'
import Image from '@/components/image'
import type {RebrandSignupModalQuery} from '@/graphql-types'
import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import useSignup from '@/hooks/use-signup'
import type {Args} from '@/hooks/use-signup'
import {useGlobalStrings} from '@/providers/global-strings'
import {useGoogleReCaptcha} from '@/providers/google-recaptcha'
import getEnvironmentField from '@/utils/get-environment-field'

export interface RebrandSignupModalProps {
    isOpen?: boolean
    onDismiss: () => void
    signupProps: Args
    onSignup?: (value: boolean) => void
}

export const RebrandSignupModal = ({
    isOpen,
    onDismiss,
    signupProps,
    onSignup,
}: RebrandSignupModalProps) => {
    const {allPrismicSignupmodal} = useStaticQuery<RebrandSignupModalQuery>(query)
    const [content] = useLocalizedContent(allPrismicSignupmodal.nodes)
    const globalStrings = useGlobalStrings()

    const modalGroup = content.data.signup_modal?.[0]
    const title = (signupProps.isPartner ? modalGroup?.partner_title : modalGroup?.title) || ''
    const subtitle =
        (signupProps.isPartner ? modalGroup?.partner_subtitle : modalGroup?.subtitle) || undefined

    const formConfig = getEnvironmentField({
        production: content.data.form_production,
        staging: content.data.form_staging,
    })

    const signup = useSignup(signupProps, formConfig.formId)
    const errorMessage = useEmailValidationErrorMessage(signup.error)
    const pendingSubmitRef = useRef(false)
    const [submitAttempt, setSubmitAttempt] = useState(0)
    const {loadReCaptcha} = useGoogleReCaptcha()

    // The new SignupModal only calls onSubmit once, with the final value — it never fires
    // handleEmailChange. We set the hook's own `email` state first, then defer the actual
    // submit to this effect so `handleSignup` reads the fresh value once React commits it.
    // Keyed on `submitAttempt` (an incrementing counter) rather than `signup.email`: React bails
    // out of re-rendering when a state setter is called with an `Object.is`-equal value, so
    // resubmitting the same email after an error would never re-trigger this effect if it were
    // keyed on the email value itself.
    useEffect(() => {
        if (pendingSubmitRef.current) {
            pendingSubmitRef.current = false
            signup.handleSubmit(undefined)
        }
        // Must re-run only on a new submit attempt (post-setEmail), not on every render `signup`'s
        // object identity changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
    }, [submitAttempt])

    useEffect(() => {
        if (isOpen) {
            loadReCaptcha()
            signupFormModalOpened({action: SignupFormModalOpenedAction.Opened})
        }
        // loadReCaptcha's identity changes once the recaptcha script finishes loading (its
        // provider recomputes the memo backing it), so including it here would re-run this
        // effect and double-fire the "modal opened" analytics event. Only isOpen should retrigger.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above
    }, [isOpen])

    useEffect(() => {
        if (onSignup) {
            onSignup(signup.loading)
        }
    }, [signup.loading, onSignup])

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            signup.clearError()
            signup.setEmail('')
            onDismiss()
        }
    }

    const handleSubmit = ({email}: {email: string}) => {
        signupFormSubmitted({marketingConsent: false, privacyConsent: false})
        pendingSubmitRef.current = true
        signup.setEmail(email)
        setSubmitAttempt((attempt) => attempt + 1)
    }

    const benefits = (content.data.items || [])
        .filter((item): item is NonNullable<typeof item> => Boolean(item?.value_item))
        .map((item) => ({
            icon: <Image image={item.image} width={24} height={24} />,
            label: item.value_item as string,
        }))

    const asideImage = content.data.side_image?.url ? (
        <Image image={content.data.side_image} classname="h-full w-full" objectFit="cover" />
    ) : null

    return (
        <SignupModal
            open={Boolean(isOpen)}
            onOpenChange={handleOpenChange}
            closeLabel={globalStrings.close_button_text}
            tag={
                modalGroup?.rating_image?.url ? (
                    <Image image={modalGroup.rating_image} width={96} />
                ) : undefined
            }
            title={title}
            subtitle={subtitle}
            onSubmit={handleSubmit}
            emailLabel={globalStrings.work_email}
            emailRequiredMessage={globalStrings.this_field_is_required}
            emailInvalidMessage={globalStrings.invalid_email_error}
            submitLabel={globalStrings.submit}
            error={errorMessage ? String(errorMessage) : undefined}
            loading={signup.loading}
            benefits={benefits.length > 0 ? benefits : undefined}
            aside={
                asideImage
                    ? {
                          image: asideImage,
                          testimonial: content.data.quote
                              ? {
                                    quote: content.data.quote,
                                    logo: content.data.logo?.url ? (
                                        <Image image={content.data.logo} width={80} height={80} />
                                    ) : undefined,
                                }
                              : undefined,
                      }
                    : undefined
            }
        />
    )
}

const query = graphql`
    query RebrandSignupModal {
        allPrismicSignupmodal {
            nodes {
                lang
                data {
                    form_production {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    form_staging {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    signup_modal {
                        title
                        subtitle
                        partner_title
                        partner_subtitle
                        rating_image {
                            dimensions {
                                width
                                height
                            }
                            alt
                            copyright
                            url
                            gatsbyImageData(width: 96)
                        }
                    }
                    items {
                        value_item
                        image {
                            dimensions {
                                width
                                height
                            }
                            alt
                            copyright
                            url
                            gatsbyImageData(width: 24)
                        }
                    }
                    logo {
                        dimensions {
                            width
                            height
                        }
                        alt
                        copyright
                        url
                        gatsbyImageData(width: 80)
                    }
                    quote
                    side_image {
                        dimensions {
                            width
                            height
                        }
                        alt
                        copyright
                        url
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }
        }
    }
`
